// Pure geometry for the floating chat window: clamping, corner-resize math, and the
// fling-to-edge snap decision. Kept side-effect free (viewport is passed in, never read from
// `window`) so it can be unit-tested without a DOM. The React component owns the pointer
// plumbing and localStorage; this module owns the arithmetic.

export const MARGIN = 24;
// Keep the window below the app's top navbar (mirrors FloatingAIChat's original TOP_INSET).
export const TOP_INSET = 96;
export const MIN_W = 340;
export const MIN_H = 440;
export const DEFAULT_SIZE: Size = { width: 400, height: 600 };
// Horizontal pointer speed (px per ms) at release above which a drag docks to that edge.
// A deliberate flick clears ~1 px/ms; a normal reposition drag stays well under it.
export const SNAP_VELOCITY = 1;
// Dock-animation duration, shared by the CSS transition and its cleanup timeout so the two
// can't drift apart.
export const SNAP_MS = 180;

export type Pos = { x: number; y: number };
export type Size = { width: number; height: number };
export type Viewport = { width: number; height: number };
export type Corner = 'nw' | 'ne' | 'sw' | 'se';

export const maxX = (size: Size, vp: Viewport): number => Math.max(0, vp.width - size.width);
export const maxY = (size: Size, vp: Viewport): number => Math.max(TOP_INSET, vp.height - size.height);

export const clampPos = (p: Pos, size: Size, vp: Viewport): Pos => ({
	x: Math.min(Math.max(0, p.x), maxX(size, vp)),
	y: Math.min(Math.max(TOP_INSET, p.y), maxY(size, vp))
});

export const clampSize = (s: Size, vp: Viewport): Size => ({
	width: Math.max(MIN_W, Math.min(s.width, vp.width - MARGIN)),
	height: Math.max(MIN_H, Math.min(s.height, vp.height - TOP_INSET - MARGIN))
});

export const defaultPos = (size: Size, vp: Viewport): Pos =>
	clampPos({ x: vp.width - size.width - MARGIN, y: TOP_INSET }, size, vp);

// Where a horizontal fling should dock the window, or null to keep it free-floating.
// Snapping preserves the window's size — it docks against the left/right edge with a MARGIN gap
// (the same breathing room the default placement uses), not flush to the very edge.
export const snapX = (velocityX: number, size: Size, vp: Viewport): number | null => {
	if (velocityX <= -SNAP_VELOCITY) return MARGIN;
	if (velocityX >= SNAP_VELOCITY) return maxX(size, vp) - MARGIN;
	return null;
};

// New rect after dragging one corner by (dx, dy). The opposite corner stays anchored; the
// dragged edges are clamped to the viewport and to the min size (the anchor never moves).
export const resizeRect = (
	corner: Corner,
	start: { pos: Pos; size: Size },
	dx: number,
	dy: number,
	vp: Viewport
): { pos: Pos; size: Size } => {
	const startRight = start.pos.x + start.size.width;
	const startBottom = start.pos.y + start.size.height;

	const movesLeftEdge = corner === 'nw' || corner === 'sw';
	const movesTopEdge = corner === 'nw' || corner === 'ne';

	let left = movesLeftEdge ? start.pos.x + dx : start.pos.x;
	let right = movesLeftEdge ? startRight : startRight + dx;
	let top = movesTopEdge ? start.pos.y + dy : start.pos.y;
	let bottom = movesTopEdge ? startBottom : startBottom + dy;

	// Clamp the moving edges to the viewport (the anchored edges already sit inside it).
	left = Math.max(0, left);
	right = Math.min(vp.width, right);
	top = Math.max(TOP_INSET, top);
	bottom = Math.min(vp.height, bottom);

	// Enforce the min size by pushing the moving edge back toward the pointer; the anchor holds.
	if (right - left < MIN_W) {
		if (movesLeftEdge) left = right - MIN_W;
		else right = left + MIN_W;
	}
	if (bottom - top < MIN_H) {
		if (movesTopEdge) top = bottom - MIN_H;
		else bottom = top + MIN_H;
	}

	return { pos: { x: left, y: top }, size: { width: right - left, height: bottom - top } };
};
